import React from 'react';
import { Badge, Table } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import { EMPTY } from '../../lib/format';
import { DEFAULT_VISIT_SORT, num } from '../../lib/aiPricingView';
import { Code, CountCell, MarginBadge, useEnumLabel } from './shared';
import { EarnedCell, PaidCell, useEarnedUnit, usePaidUnit } from './VisitCells';

/**
 * The visits, one row per appointment (PM-0427) — server-sorted and
 * server-paged, like the ledger.
 *
 * Identity is ids only, by decision (D8): the appointment id and the payer's
 * id, each an LTR island in Latin digits. No name, no patient id — the patient
 * is exactly what this page must not learn. A visit paid by more than one
 * payer carries a flag, because then its row is not one doctor's.
 */
export default function VisitsTable({ rows, conv, view, onSort, onOpen, empty }) {
  const { t, formatDateTime, formatNumber } = useTranslation();
  const ownerLabel = useEnumLabel('aiPricing.overview.owner');
  const earnedUnit = useEarnedUnit(conv);
  const paidUnit = usePaidUnit(conv);

  const minutes = (v) => (num(v) == null ? EMPTY : formatNumber(num(v), { maximumFractionDigits: 1 }));

  const columns = [
    {
      key: 'started',
      header: t('aiPricing.visits.table.columns.time'),
      sortable: true,
      width: '11rem',
      render: (r) => <span className="tabular-nums">{formatDateTime(r.firstAt)}</span>,
    },
    {
      key: 'owner',
      header: t('aiPricing.visits.table.columns.owner'),
      width: '12rem',
      render: (r) => (
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            {ownerLabel(r.ownerType)}
            {(r.owners || 0) > 1 ? (
              <Badge variant="warning" title={t('aiPricing.visits.multiOwnerHint')}>
                {t('aiPricing.visits.multiOwner', { count: r.owners })}
              </Badge>
            ) : null}
          </div>
          <Code title={r.ownerId || undefined}>{r.ownerId ? String(r.ownerId).slice(0, 12) : null}</Code>
        </div>
      ),
    },
    {
      key: 'appointment',
      header: t('aiPricing.visits.table.columns.appointment'),
      width: '12rem',
      render: (r) => <Code title={r.appointmentId}>{String(r.appointmentId).slice(0, 14)}</Code>,
    },
    {
      key: 'minutes',
      header: t('aiPricing.visits.table.columns.minutes'),
      sortable: true,
      align: 'end',
      width: '6rem',
      render: (r) => (
        <span className="inline-flex flex-col items-end gap-0.5">
          <span className="tabular-nums">{minutes(r.billedMinutes)}</span>
          {r.recordings ? (
            <span className="text-[11px] text-muted-foreground">{t('aiPricing.visits.table.recordings', { count: r.recordings })}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'runs',
      header: t('aiPricing.visits.table.columns.runs'),
      align: 'end',
      width: '6rem',
      render: (r) => (
        <span className="inline-flex flex-col items-end gap-0.5">
          <CountCell value={r.runs} />
          <span className="text-[11px] text-muted-foreground">{t('aiPricing.visits.table.charged', { count: r.chargedRuns ?? 0 })}</span>
        </span>
      ),
    },
    {
      key: 'earned',
      header: t('aiPricing.visits.table.columns.earned', { unit: earnedUnit }),
      sortable: true,
      align: 'end',
      width: '9rem',
      render: (r) => <EarnedCell parseh={r.earnedParseh} conv={conv} data-testid={`ai-visits-row-${r.appointmentId}-earned`} />,
    },
    {
      key: 'paid',
      header: t('aiPricing.visits.table.columns.paid', { unit: paidUnit }),
      sortable: true,
      align: 'end',
      width: '9rem',
      render: (r) => <PaidCell usd={r.paidUsd} conv={conv} partial={(r.unpricedRuns || 0) > 0} data-testid={`ai-visits-row-${r.appointmentId}-paid`} />,
    },
    {
      key: 'margin',
      header: t('aiPricing.visits.table.columns.margin'),
      sortable: true,
      align: 'end',
      width: '6rem',
      render: (r) => <MarginBadge value={r.marginPct} data-testid={`ai-visits-row-${r.appointmentId}-margin`} />,
    },
  ];

  return (
    <Table
      dense
      ariaLabel={t('aiPricing.visits.table.aria')}
      columns={columns}
      rows={rows}
      rowKey={(r) => r.appointmentId}
      sort={{ key: view.sort || DEFAULT_VISIT_SORT, dir: view.dir === 'asc' ? 'asc' : 'desc' }}
      onSort={onSort}
      onRowActivate={onOpen}
      rowProps={(r) => ({ 'data-testid': `ai-visits-row-${r.appointmentId}`, 'aria-label': t('aiPricing.visits.table.open', { id: r.appointmentId }) })}
      empty={empty}
      data-testid="ai-visits-table"
    />
  );
}
